/**
 * Client typé pour les routes `/invitations` — Phase 6, incrément 6.2.
 *
 * 3 méthodes :
 *
 *   - `createInvitation(homeId, {email, role})` — **offline-first**
 *     via `sendOrQueueMutation` (pattern 3.3.3). Si offline, la
 *     mutation est queued ; le replay réémettra à la reconnexion
 *     et le serveur enverra l'email à ce moment-là.
 *   - `getInvitationPreview(token)` — lecture publique, **pas
 *     offline-first** : on a besoin du serveur pour valider le
 *     token, pas d'intérêt à queueing si offline. L'UI affiche un
 *     fallback « réseau indisponible » dans ce cas.
 *   - `acceptInvitation(token)` — **pas offline-first** : l'acceptation
 *     nécessite une transaction serveur (crée membership) + email
 *     match côté serveur. Si offline, on bloque côté UI plutôt
 *     que queueing un POST qui pourrait échouer au replay (401 si
 *     session expirée, 403 si email change, etc.).
 */
import { apiRequest } from './client.js';
import { sendOrQueueMutation, type SendResult } from './mutationsReplay.js';

export type InvitationRole = 'parent' | 'teen' | 'child';

export interface ApiInvitation {
  id: string;
  homeId: string;
  email: string;
  role: InvitationRole;
  token: string;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
  /** URL absolue d'acceptation (ex. `https://nestor.app/#/invite/<token>`). */
  acceptUrl: string;
}

/**
 * Preview publique d'une invitation (GET `/invitations/:token`).
 * **Pas d'email** — l'invité ne voit que `homeName` + `inviterName`
 * pour décider d'accepter ou pas (no-leak côté énumération).
 */
export interface ApiInvitationPreview {
  homeName: string;
  homeMode: 'classic' | 'coparenting';
  inviterName: string | null;
  role: InvitationRole;
  expiresAt: string;
}

export interface CreateInvitationInput {
  email: string;
  role: InvitationRole;
}

export interface AcceptInvitationResult {
  homeId: string;
  role: InvitationRole;
}

/**
 * Crée une invitation pour un foyer. Seul un membre `parent` peut
 * appeler (403 sinon). Offline-first : si offline, queued ; le
 * replay réémettra et le serveur enverra l'email à ce moment.
 */
export async function createInvitation(
  homeId: string,
  input: CreateInvitationInput,
): Promise<SendResult<{ invitation: ApiInvitation }>> {
  return sendOrQueueMutation<{ invitation: ApiInvitation }>({
    method: 'POST',
    path: `/homes/${homeId}/invitations`,
    body: input,
  });
}

/**
 * Lecture publique d'une invitation par token. Throw `ApiError` :
 *   - 404 `expired_or_invalid` (token absent ou expiré).
 *   - 409 `already_accepted` (déjà utilisée).
 *   - 503 `db_unavailable` (mode dégradé).
 *
 * Le caller doit gérer ces 3 cas pour afficher des messages i18n
 * adaptés (cf. `modules/invite/View.ts`).
 */
export async function getInvitationPreview(token: string): Promise<ApiInvitationPreview> {
  const data = await apiRequest<{ invitation: ApiInvitationPreview }>(`/invitations/${token}`);
  return data.invitation;
}

/**
 * Accepte une invitation. Auth requise (cookie session). Throw
 * `ApiError` :
 *   - 401 `unauthorized` (pas connecté).
 *   - 403 `email_mismatch` (user authentifié ≠ destinataire).
 *   - 404 `expired_or_invalid`.
 *   - 409 `already_accepted`.
 *
 * Retourne `{homeId, role}` pour que le frontend bascule sur le
 * nouveau foyer après acceptation.
 */
export async function acceptInvitation(token: string): Promise<AcceptInvitationResult> {
  return apiRequest<AcceptInvitationResult>(`/invitations/${token}/accept`, {
    method: 'POST',
  });
}
